/**
 * Quand une sollicitation a le droit de revenir.
 *
 * Logique séparée du rendu, et sans horloge à elle : `maintenant` et le tirage sont des
 * paramètres. Une cadence qui lit l'heure elle-même ne se teste qu'en attendant une
 * semaine, et c'est exactement le genre de règle qu'on n'ose plus toucher ensuite.
 *
 * Le repère stocké est la **prochaine échéance**, pas le dernier affichage. Stocker le
 * dernier affichage obligerait à retirer l'intervalle au chargement, donc à tirer un
 * nombre aléatoire à chaque montage : la date de retour changerait à chaque ouverture de
 * l'application, et la sollicitation finirait par tomber un jour où elle n'était pas due.
 */

const JOUR_MS = 24 * 60 * 60 * 1000;

/**
 * Une à deux fois par semaine : un intervalle tiré entre trois jours et demi et sept.
 *
 * Le tirage est demandé plutôt que régulier pour que la sollicitation ne s'installe pas
 * dans une habitude — « tous les lundis matin » se voit, s'anticipe, et agace.
 */
export function intervalleHebdomadaire(tirage: number): number {
  const borne = Math.min(Math.max(tirage, 0), 1);
  return Math.round((3.5 + borne * 3.5) * JOUR_MS);
}

/**
 * L'échéance est-elle atteinte ?
 *
 * Un repère absent **n'affiche pas** : la première ouverture d'un compte ne doit pas
 * s'accompagner d'une sollicitation. La première échéance est posée au premier passage,
 * et c'est le passage suivant qui pourra l'honorer.
 */
export function echeanceAtteinte(prochaine: number | null, maintenant: number): boolean {
  return prochaine !== null && maintenant >= prochaine;
}

/** Lit un repère. Un stockage indisponible ou illisible vaut « pas de repère ». */
export function lireEcheance(cle: string): number | null {
  try {
    const brut = globalThis.localStorage?.getItem(cle);
    if (brut === null || brut === undefined) return null;
    const valeur = Number.parseInt(brut, 10);
    return Number.isFinite(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

/**
 * Pose la prochaine échéance.
 *
 * L'échec d'écriture est avalé : en navigation privée, le stockage jette. La conséquence
 * est qu'on ne se souvient de rien, donc qu'on n'affiche rien — jamais qu'on affiche à
 * chaque écran.
 */
export function poserEcheance(cle: string, quand: number): void {
  try {
    globalThis.localStorage?.setItem(cle, String(quand));
  } catch {
    /* stockage refusé : la sollicitation ne reviendra pas, et c'est le bon défaut */
  }
}
